<template>
  <v-card
    class="custom-video-player mx-auto"
    :width="width"
    rounded="lg"
    elevation="6"
    :style="{ backgroundColor: controlsBgColor }"
  >
    <div
      class="video-container"
      @mouseenter="showControls = true"
      @mouseleave="showControls = !isPlaying"
    >
      <video
        ref="videoPlayer"
        @loadedmetadata="setDuration"
        @timeupdate="updateTime"
        @click="togglePlay"
        class="video-element"
      >
        <source :src="src" type="video/mp4" />
        Seu navegador não suporta o elemento de vídeo.
      </video>

      <v-fade-transition>
        <div v-show="showControls || !isPlaying" class="controls-overlay">
          <div v-if="!isPlaying" class="center-play-button">
            <v-btn icon x-large dark @click="togglePlay">
              <v-icon size="64">mdi-play-circle-outline</v-icon>
            </v-btn>
          </div>

          <div class="controls-bar">
            <v-btn icon small dark @click="togglePlay" class="mr-2">
              <v-icon>{{ isPlaying ? "mdi-pause" : "mdi-play" }}</v-icon>
            </v-btn>

            <span class="white--text text-caption font-weight-medium mr-2">
              {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
            </span>

            <v-slider
              v-model="seekValue"
              :max="duration"
              @change="seekVideo"
              hide-details
              dense
              class="progress-slider"
              color="primary"
              track-color="white"
            ></v-slider>

            <div class="d-flex align-center ml-2">
              <v-btn icon small dark @click="toggleMute">
                <v-icon>{{ volumeIcon }}</v-icon>
              </v-btn>
              <v-slider
                v-model="volume"
                @input="setVolume"
                :max="1"
                :step="0.01"
                hide-details
                dense
                style="width: 60px"
                color="white"
                track-color="grey lighten-1"
              ></v-slider>
            </div>

            <v-btn icon small dark @click="toggleFullscreen" class="ml-2">
              <v-icon>mdi-fullscreen</v-icon>
            </v-btn>
          </div>
        </div>
      </v-fade-transition>
    </div>
  </v-card>
</template>

<script>
export default {
  name: "CustomVideoPlayer",
  props: {
    src: {
      type: String,
      required: true,
      // Exemplo: 'https://storage.googleapis.com/pitch-connect-documents/...'
    },
    width: {
      type: [String, Number],
      default: "100%",
    },
    controlsBgColor: {
      type: String,
      default: "#212121", // Cinza escuro
    },
  },
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      volume: 1, // 0 a 1
      showControls: true,
      lastVolume: 1,
    };
  },
  computed: {
    video() {
      // Acessa a referência ao elemento DOM do vídeo
      return this.$refs.videoPlayer;
    },
    volumeIcon() {
      // CORREÇÃO: Verifica se this.video existe antes de acessar 'muted'
      if (!this.video) return "mdi-volume-off";

      if (this.volume === 0 || this.video.muted) return "mdi-volume-off";
      if (this.volume < 0.5) return "mdi-volume-low";
      return "mdi-volume-high";
    },
    seekValue: {
      get() {
        return this.currentTime;
      },
      set(val) {
        this.currentTime = val;
      },
    },
  },
  methods: {
    togglePlay() {
      if (!this.video) return; // ✅ Verificação de segurança

      if (this.video.paused || this.video.ended) {
        this.video.play();
        this.isPlaying = true;
        this.showControls = false;
      } else {
        this.video.pause();
        this.isPlaying = false;
        this.showControls = true;
      }
    },
    updateTime() {
      if (!this.video) return; // ✅ Verificação de segurança
      this.currentTime = this.video.currentTime;
    },
    setDuration() {
      if (!this.video) return; // ✅ Verificação de segurança
      this.duration = this.video.duration;
      this.setVolume(this.volume);
    },
    seekVideo(value) {
      if (!this.video) return; // ✅ Verificação de segurança
      this.video.currentTime = value;
    },
    setVolume(value) {
      if (!this.video) return; // ✅ Verificação de segurança
      this.video.volume = value;
      this.volume = value;
      if (value > 0) this.lastVolume = value;
    },
    toggleMute() {
      if (!this.video) return; // ✅ Verificação de segurança
      if (this.volume > 0) {
        this.lastVolume = this.volume;
        this.setVolume(0);
      } else {
        this.setVolume(this.lastVolume);
      }
    },
    toggleFullscreen() {
      // Pede Fullscreen para o componente v-card inteiro
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else if (this.$el.requestFullscreen) {
        this.$el.requestFullscreen();
      } else if (this.video && this.video.webkitEnterFullscreen) {
        // iOS/Safari
        this.video.webkitEnterFullscreen();
      }
    },
    formatTime(time) {
      if (isNaN(time)) return "00:00";
      const hours = Math.floor(time / 3600);
      const minutes = Math.floor((time % 3600) / 60);
      const seconds = Math.floor(time % 60);

      let formatted = `${String(minutes).padStart(2, "0")}:${String(
        seconds
      ).padStart(2, "0")}`;
      if (hours > 0) {
        formatted = `${String(hours).padStart(2, "0")}:${formatted}`;
      }
      return formatted;
    },
  },
  mounted() {
    // CORREÇÃO: Usamos $nextTick para garantir que o elemento DOM exista antes de interagir com ele
    this.$nextTick(() => {
      if (this.video) {
        this.video.volume = this.volume;

        // Adiciona Listeners
        this.video.addEventListener("play", () => {
          setTimeout(() => {
            if (this.isPlaying) this.showControls = false;
          }, 1500);
        });

        this.video.addEventListener("ended", () => {
          this.isPlaying = false;
          this.showControls = true;
        });
      }
    });
  },
};
</script>

<style scoped>
.custom-video-player {
  /* Fixa o aspecto para que o vídeo não mude de tamanho drasticamente */
  min-height: 350px;
}

.video-container {
  position: relative;
  width: 100%;
  overflow: hidden;
  /* Garante que os cantos arredondados da v-card se apliquem ao container */
  border-radius: inherit;
}

.video-element {
  display: block;
  width: 100%;
  height: 100%;
  cursor: pointer;
}

.controls-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4); /* Fundo semi-transparente */
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
  z-index: 10;
}

/* Botão de Play Central */
.center-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

/* Barra de Controles (Rodapé) */
.controls-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 16px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);
}

.progress-slider {
  /* Garante que o slider ocupe o espaço central */
  flex-grow: 1;
  margin: 0 10px;
  padding-top: 10px; /* Ajusta o alinhamento visual com os botões */
}

/* Aumenta a área de toque para botões pequenos */
.v-btn--small.v-btn--icon {
  min-width: 32px;
}
</style>
